import { useState, useEffect, useCallback } from 'react';
import { costEstimationService } from '../services';
import { CostEstimationResponse } from '../types';

interface ApiCostEstimationParams {
  analysisType: 'single' | 'multiple' | 'bulk';
  // Für Provider-basierte Analyse
  providerIds?: string[];
  // Für Contract-basierte Analyse  
  providerId?: string;
  contractIds?: string[];
  // Für einzelne Analyse
  contractId?: string;
}

interface ApiCostEstimationResult {
  loading: boolean;
  error: string | null;
  data: CostEstimationResponse['data'] | null;
  refetch: () => Promise<void>;
}

export const useApiAnalysisCost = (params: ApiCostEstimationParams): ApiCostEstimationResult => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CostEstimationResponse['data'] | null>(null);

  const fetchEstimation = useCallback(async () => {
    const { analysisType, providerIds, providerId, contractIds, contractId } = params;
    
    // Validierung der Parameter
    if (analysisType === 'bulk' && (!providerIds || providerIds.length === 0)) {
      setError('Provider IDs sind erforderlich für Bulk-Analyse');
      return;
    }
    
    if (analysisType === 'multiple' && (!providerId || !contractIds || contractIds.length === 0)) {
      setError('Provider ID und Contract IDs sind erforderlich für Mehrfach-Analyse');
      return;
    }
    
    if (analysisType === 'single' && (!providerId || !contractId)) {
      setError('Provider ID und Contract ID sind erforderlich für Einzel-Analyse');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response: CostEstimationResponse;

      switch (analysisType) {
        case 'bulk':
          response = await costEstimationService.estimateServiceProvidersAnalysis(providerIds!);
          break;
        case 'multiple':
          response = await costEstimationService.estimateContractsAnalysis(providerId!, contractIds!);
          break;
        case 'single':
          response = await costEstimationService.estimateSingleContractAnalysis(providerId!, contractId!);
          break;
        default:
          throw new Error(`Unbekannter Analyse-Typ: ${analysisType}`);
      }

      if (response.status === 'success' && response.data) {
        setData(response.data);
        setError(null);
      } else {
        setError(response.message || 'Fehler bei der Kostenschätzung');
        setData(null);
      }
    } catch (err: any) {
      console.error('Cost estimation error:', err);
      setError(err?.response?.data?.message || err?.message || 'Fehler bei der Kostenschätzung');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [
    params.analysisType,
    params.providerId,
    params.contractId,
    JSON.stringify(params.providerIds), // Stabiler String für Array-Vergleich
    JSON.stringify(params.contractIds)  // Stabiler String für Array-Vergleich
  ]);

  // Kein Auto-fetch mehr - nur manuell über refetch()

  return {
    loading,
    error,
    data,
    refetch: fetchEstimation
  };
}; 